import React, { useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';

// Biorritmo: fórmulas puras sin(2π · idade_dias / período) — físico 23, emocional 28, intelectual 33
const PERIODS = [
  { key: 'físico', days: 23, color: '#8FE2AE', emoji: '💪' },
  { key: 'emocional', days: 28, color: '#D9C8FF', emoji: '💗' },
  { key: 'intelectual', days: 33, color: '#E8C87A', emoji: '🧠' },
] as const;

function waveValue(ageDays: number, period: number): number {
  return Math.sin((2 * Math.PI * ageDays) / period);
}

function interpret(value: number, label: string): string {
  if (value > 0.5) return `${label} em alta — bom dia para arriscar`;
  if (value > 0) return `${label} subindo — ritmo favorável`;
  if (value > -0.5) return `${label} em descida — paciência`;
  return `${label} em baixa — dia de conservar`;
}

interface BioData {
  values: number[];
  svgPath: string[];
  todayX: number;
  ageDays: number;
}

export const BiorhythmPage: React.FC = () => {
  const { user } = useAuth();
  const [birth, setBirth] = useState('');
  const effectiveBirth = birth || (user?.birth_date ?? '');

  const bio = useMemo<BioData | null>((): BioData | null => {
    if (!effectiveBirth) return null;
    const born = new Date(effectiveBirth + 'T12:00:00');
    if (Number.isNaN(born.getTime())) return null;
    const today = new Date();
    const age = (today.getTime() - born.getTime()) / 86400000;

    const vals = PERIODS.map(p => waveValue(age, p.days));

    // 30 dias: -15 até +15 a partir de hoje
    const width = 600;
    const step = width / 30;
    const paths: string[] = [];
    for (const p of PERIODS) {
      let d = '';
      for (let i = 0; i <= 30; i++) {
        const v = waveValue(age - 15 + i, p.days);
        const x = (i * step).toFixed(1);
        const y = (50 - v * 45).toFixed(1);
        d += `${i === 0 ? 'M' : 'L'}${x},${y} `;
      }
      paths.push(d);
    }
    return { values: vals, svgPath: paths, todayX: 15 * step, ageDays: age };
  }, [effectiveBirth]);

  const values = bio?.values;
  const svgPath = bio?.svgPath;

  return (
    <div className="min-h-[calc(100vh-4rem)] text-stela-fg">
      <main className="max-w-[760px] mx-auto px-4 py-8">
        <Link to="/" className="text-stela-muted text-sm hover:text-stela-lilac">← Voltar</Link>
        <h1 className="font-display font-semibold text-3xl mt-3">
          Biorritmo {effectiveBirth ? 'de hoje' : '— seu dia em ciclos'}
        </h1>
        <p className="text-stela-muted text-sm mt-2">
          Três ciclos clássicos — físico (23 dias), emocional (28) e intelectual (33) — calculados
          diretamente desde a sua data de nascimento.
        </p>

        {!effectiveBirth && (
          <form
            onSubmit={(e) => e.preventDefault()}
            className="bg-stela-card border border-stela-line rounded-2xl p-5 mt-6 flex flex-wrap gap-3 items-end"
          >
            <div className="flex-1 min-w-[220px]">
              <label htmlFor="bio-birth" className="block text-xs text-stela-muted mb-1">
                Sua data de nascimento
              </label>
              <input
                id="bio-birth"
                type="date"
                value={birth}
                onChange={(e) => setBirth(e.target.value)}
                className="w-full bg-white/5 border border-stela-line rounded-[10px] text-stela-fg px-3 py-2.5"
              />
            </div>
            {!user && (
              <Link to="/" className="text-stela-gold text-sm hover:underline">
                Crie sua conta para salvar
              </Link>
            )}
          </form>
        )}

        {values && svgPath && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-6">
              {PERIODS.map((period, idx) => (
                <div key={period.key} className="bg-stela-card border border-stela-line rounded-2xl p-4">
                  <div className="flex items-center gap-2">
                    <span>{period.emoji}</span>
                    <h3 className="font-medium capitalize">{period.key}</h3>
                  </div>
                  <p className="font-display text-3xl mt-2" style={{ color: period.color }}>
                    {values[idx] >= 0 ? '+' : ''}
                    {(values[idx] * 100).toFixed(0)}%
                  </p>
                  <p className="text-stela-muted text-xs mt-1">{interpret(values[idx], period.key)}</p>
                </div>
              ))}
            </div>

            <div className="bg-stela-card border border-stela-line rounded-2xl p-5 mt-6">
              <h2 className="font-display text-lg mb-3">Seus 30 dias</h2>
              <svg viewBox="0 0 600 100" className="w-full h-auto" role="img" aria-label="Ondas do biorritmo por 30 dias">
                <line x1="0" y1="50" x2="600" y2="50" stroke="rgba(190,160,255,.2)" strokeWidth="1" />
                {svgPath.map((d, i) => (
                  <path
                    key={PERIODS[i].key}
                    d={d}
                    fill="none"
                    stroke={PERIODS[i].color}
                    strokeWidth="2"
                    opacity={0.9}
                  />
                ))}
                <line
                  x1={bio.todayX}
                  y1="4"
                  x2={bio.todayX}
                  y2="96"
                  stroke="#F1EDFF"
                  strokeWidth="1.5"
                  strokeDasharray="3 4"
                />
              </svg>
              <div className="flex gap-4 mt-2 text-xs text-stela-muted">
                {PERIODS.map((period) => (
                  <span key={period.key} style={{ color: period.color }}>
                    — {period.key}
                  </span>
                ))}
                <span className="text-stela-fg">| hoje</span>
              </div>
              <p className="text-stela-muted text-[11.5px] mt-2">
                Dia {Math.floor(bio.ageDays)} de vida · cálculo direto das fórmulas clássicas (Swift 1979) —
                nenhum dado sai do seu navegador.
              </p>
            </div>
          </>
        )}
      </main>
    </div>
  );
};